import { deliveryControls } from '@bb/contracts';
import { UNIT_SECONDS } from '../trigger/actionBased.ts';

export type DeliveryControls = deliveryControls.DeliveryControls;
export type MessageExemptions = deliveryControls.MessageExemptions;
export type Reeligibility = deliveryControls.Reeligibility;
export type QuietHours = deliveryControls.QuietHours;
export type ExemptionFlag = (typeof deliveryControls.BYPASS_FLAGS)[number] | 'countTowardsCaps';

/** The two parts of the draft content the form edits: `content.deliveryControls` and `content.exemptions`. */
export interface DeliveryControlsValue {
  deliveryControls: DeliveryControls;
  exemptions: MessageExemptions;
}

export const REELIGIBILITY_MAX_DAYS = deliveryControls.REELIGIBILITY_MAX_SECONDS / UNIT_SECONDS.days;
export const RATE_DELAY_MAX_HOURS = deliveryControls.RATE_DELAY_MAX_HOURS;
export const MAX_RATE_PER_MINUTE = 1_000_000;

export type ReeligibilityMode = 'once' | 'always' | 'after';

/**
 * How the stored rule reads: no rule is «always» (campaigns published before BB-102 keep sending as they did), a zero
 * window too; a window still being typed (`NaN`) stays «after».
 */
export function reeligibilityMode(rule: Reeligibility | undefined): ReeligibilityMode {
  if (!rule) return 'always';
  if (!rule.enabled) return 'once';
  return rule.windowSeconds === 0 ? 'always' : 'after';
}

/** The rule of another mode; «after» starts from one day or keeps the window already set. */
export function switchReeligibility(rule: Reeligibility | undefined, mode: ReeligibilityMode): Reeligibility {
  if (mode === 'once') return { enabled: false, windowSeconds: 0 };
  if (mode === 'always') return { enabled: true, windowSeconds: 0 };
  return { enabled: true, windowSeconds: rule?.enabled && rule.windowSeconds > 0 ? rule.windowSeconds : UNIT_SECONDS.days };
}

export const DEFAULT_QUIET_HOURS: QuietHours = { enabled: true, from: '22:00', to: '08:00', fallback: 'next_available' };

/** Quiet hours whose end is earlier than their start run over midnight (22:00–08:00). */
export const spansMidnight = (quiet: QuietHours): boolean => quiet.from > quiet.to;

/** A field set or removed, so «not set» stays absent in the stored draft. */
export function withControl<K extends keyof DeliveryControls>(controls: DeliveryControls, key: K, value: DeliveryControls[K] | undefined): DeliveryControls {
  const next = { ...controls };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

const MESSAGES: Record<string, string> = {
  'reeligibility.windowSeconds': `Enter a wait from 1 minute to ${REELIGIBILITY_MAX_DAYS} days.`,
  'quietHours.from': 'Enter a start time.',
  'quietHours.to': 'Enter an end time.',
  ratePerMinute: `Enter a whole number from 1 to ${MAX_RATE_PER_MINUTE.toLocaleString('en-US')}.`,
  rateMaxDelayHours: `Enter a whole number of hours from 1 to ${RATE_DELAY_MAX_HOURS}.`,
};

/** Client-side check of the contract (`deliveryControls.DeliveryControls`), one message per field path (`quietHours.to`). */
export function deliveryControlsIssues(value: DeliveryControls): Record<string, string> {
  const parsed = deliveryControls.DeliveryControls.safeParse(value);
  if (parsed.success) return {};
  const issues: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const field = issue.path.join('.') || 'deliveryControls';
    const message = field === 'quietHours.to' && issue.message === 'quiet hours need a start and an end that differ' ? 'The end must differ from the start.' : (MESSAGES[field] ?? issue.message);
    issues[field] ??= message;
  }
  return issues;
}

/** What each exemption lets a message skip, in form order. */
export const EXEMPTIONS: ReadonlyArray<{ flag: ExemptionFlag; label: string; hint: string }> = [
  { flag: 'bypassSuppression', label: 'Send to users in suppression lists', hint: 'Users in an active suppression list get it too.' },
  { flag: 'bypassGlobalControl', label: 'Send to the global control group', hint: 'Users held out of all messaging get it too, which blurs what the control group measures.' },
  { flag: 'bypassFrequencyCap', label: 'Skip frequency caps', hint: 'Sends even to users who have reached a frequency capping rule.' },
  { flag: 'bypassQuietHours', label: 'Skip quiet hours', hint: 'Sends during the quiet hours above.' },
  { flag: 'bypassRateLimit', label: 'Skip rate limits', hint: "Ignores the campaign's and the environment's sends-per-minute limits." },
  { flag: 'countTowardsCaps', label: 'Count towards frequency caps', hint: 'When off, sends are still checked against caps but do not use them up.' },
];
